import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { debounce } from 'lodash';
import { useAuth } from '../../../contexts/AuthContext';
import { auth, db } from '../../../services/firebase';
import { ref, onValue, update, query, orderByChild, equalTo, limitToLast } from 'firebase/database';
import NotificationBadge from '../UI/NotificationBadge';
import './Navbar.css';
import { getMessaging, getToken, isSupported } from 'firebase/messaging';

function Navbar() {
  const [click, setClick] = useState(false);
  const [button, setButton] = useState(true);
  const [userName, setUserName] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [newApplicationsCount, setNewApplicationsCount] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [error, setError] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);

  const { currentUser, verified, hasRole } = useAuth();
  const navigate = useNavigate();

  const isAdmin = hasRole('admin') || hasRole('superAdmin');

  const handleClick = () => setClick(!click);
  const closeMobileMenu = () => setClick(false);

  const showButton = () => {
    setButton(window.innerWidth > 960);
  };

  const handleSignOut = async () => {
    try {
      await auth.signOut();
      navigate('/');
    } catch (error) {}
  };

  const getInitials = () => {
    const names = userName.split(' ');
    return names.map(name => name[0] || '').join('').toUpperCase();
  };

  const handleProfileImageError = () => setImageUrl('');

  const setupNotifications = async () => {
    try {
      const isMessagingSupported = await isSupported();
      if (isMessagingSupported) {
        const messaging = getMessaging();
        await getToken(messaging, {
          vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY
        });
      }
    } catch {}
  };

  useEffect(() => {
    if (!currentUser || !verified) {
      setUserName('');
      setImageUrl('');
      return;
    }

    // Set image URL from auth context
    setImageUrl(currentUser.photoURL || '');

    // Fetch user name from Realtime Database
    const userRef = ref(db, `users/${currentUser.uid}`);
    const unsubscribeName = onValue(userRef, (snapshot) => {
      if (snapshot.exists()) {
        const userData = snapshot.val();
        setUserName(`${userData.firstName || ''} ${userData.lastName || ''}`.trim());
        // Image URL is now set directly from currentUser.photoURL
        // If you still want to use the database URL as a fallback or primary, you can set it here:
        // setImageUrl(userData.profileImageUrl || currentUser.photoURL || ''); 
      } else {
        // Handle case where user data might not be in DB yet or name is not set
        setUserName(currentUser.displayName || 'User'); 
      }
    }, (error) => {
      // Fallback to displayName from auth if RTDB fetch fails
      setUserName(currentUser.displayName || 'User');
    });

    return () => {
      unsubscribeName();
    };
  }, [currentUser, verified]);

  useEffect(() => {
    if (!currentUser || !isAdmin || !verified) {
      setNewApplicationsCount(0);
      setNotifications([]);
      setUnreadCount(0);
      return;
    }

    setupNotifications();
    const interval = setInterval(setupNotifications, 30000);
    return () => clearInterval(interval);
  }, [currentUser, isAdmin, verified]);

  const handleNotificationClick = async (notification, e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await update(ref(db, `notifications/${notification.id}`), {
        read: true,
        readAt: new Date().getTime()
      });
    } catch (error) {
    }
    setShowNotifications(false);

    if (notification.type === 'partnership_application') {
      navigate('/admin/partnership-applications');
    } else if (notification.type === 'academy_application') {
      navigate('/admin/academy-applications');
    }
  };

  useEffect(() => {
    const closeNotifications = () => setShowNotifications(false);
    if (showNotifications) {
      document.addEventListener('click', closeNotifications);
    }
    return () => document.removeEventListener('click', closeNotifications);
  }, [showNotifications]);

  const handleProfileClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    closeMobileMenu();
    setShowDropdown(false);
    navigate('/profile');
  };

  useEffect(() => {
    const closeDropdown = () => setShowDropdown(false);
    if (showDropdown) {
      document.addEventListener('click', closeDropdown);
    }
    return () => document.removeEventListener('click', closeDropdown);
  }, [showDropdown]);

  useEffect(() => {
    const debouncedShowButton = debounce(showButton, 150);
    window.addEventListener('resize', debouncedShowButton);
    return () => {
      window.removeEventListener('resize', debouncedShowButton);
      debouncedShowButton.cancel();
    };
  }, []);

  useEffect(() => {
    if (!currentUser || !isAdmin || !verified) {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }

    const notificationsRef = ref(db, 'notifications');
    const notificationsQuery = query(
      notificationsRef,
      orderByChild('userId'),
      equalTo(currentUser.uid),
      limitToLast(50)
    );

    const unsubscribe = onValue(notificationsQuery, (snapshot) => {
      const notifs = [];
      snapshot.forEach((childSnapshot) => {
        const notification = childSnapshot.val();
        if (!notification.read) {
          notifs.push({ id: childSnapshot.key, ...notification });
        }
      });
      setNotifications(notifs);
      setUnreadCount(notifs.length);
    }, (error) => {
      setError('Failed to load notifications');
    });

    return () => unsubscribe();
  }, [currentUser, isAdmin, verified]);

  const profilePicElement = (
    <div className="navbar-profile-avatar" onClick={handleProfileClick}>
      {imageUrl ? (
        <img src={imageUrl} alt={userName} onError={handleProfileImageError} />
      ) : (
        <div className="default-avatar">{getInitials()}</div>
      )}
    </div>
  );

  return (
    <nav className="navbar">
      <div className="navbar-container">
        <Link to="/" className="navbar-logo" onClick={closeMobileMenu}>
          <img src="../images/fomeje-logo-2025.png" alt="FOMEJE Logo" className="navbar-logo-image" />
        </Link>
        <div className="menu-icon" onClick={handleClick}>
          <i className={click ? 'fas fa-times' : 'fas fa-bars'} />
        </div>
        <ul className={click ? 'nav-menu active' : 'nav-menu'}>
          <li className="nav-item"><Link to="/" className="nav-links" onClick={closeMobileMenu}>Home</Link></li>
          <li className="nav-item"><Link to="/about" className="nav-links" onClick={closeMobileMenu}>About Us</Link></li>
          <li className="nav-item"><Link to="/projects" className="nav-links" onClick={closeMobileMenu}>Projects</Link></li>
          <li className="nav-item"><Link to="/activities" className="nav-links" onClick={closeMobileMenu}>Activities</Link></li>
          <li className="nav-item"><Link to="/think-tank" className="nav-links" onClick={closeMobileMenu}>Think Tank</Link></li>
          <li className="nav-item"><Link to="/groups-and-clubs" className="nav-links" onClick={closeMobileMenu}>Our Groups</Link></li>
          <li className="nav-item"><Link to="/publications" className="nav-links" onClick={closeMobileMenu}>Publications</Link></li>

          {currentUser && verified && (
            <li className="nav-item">
              <Link to="/profile" className="nav-links" onClick={closeMobileMenu}>Profile</Link>
            </li>
          )}

          {isAdmin && currentUser && verified && (
            <li className="nav-item">
              <Link to="/admin" className="nav-links" onClick={closeMobileMenu}>Admin Dashboard</Link>
            </li>
          )}

          <li className="nav-item">
            {currentUser && verified ? (
              <button className="btn-sign-in" onClick={handleSignOut}>Sign Out</button>
            ) : (
              <Link className="btn-sign-in" to="/sign-in" onClick={closeMobileMenu}>Sign In</Link>
            )}
          </li>
        </ul>

        {currentUser && verified && (
          <div className="profile-section">
            <div className="profile-pic-container">
              {isAdmin && (
                <div className="notifications-wrapper" onClick={e => { e.stopPropagation(); setShowNotifications(!showNotifications); }}>
                  <NotificationBadge count={unreadCount} />
                  {showNotifications && notifications.length > 0 && (
                    <div className="notifications-dropdown">
                      {notifications.map(notification => (
                        <div key={notification.id} className="notification-item" onClick={(e) => handleNotificationClick(notification, e)}>
                          <div className="notification-content">
                            <strong>{notification.type === 'partnership_application' ? 'New Partnership Application' : 'New Academy Application'}</strong>
                            <p>{notification.type === 'partnership_application' ? `From: ${notification.organizationName}` : `Application ID: ${notification.applicationId}`}</p>
                            <small>{new Date(notification.createdAt).toLocaleString()}</small>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {profilePicElement}
            </div>
          </div>
        )}
        {error && <div className="error-message">{error}</div>}
      </div>
    </nav>
  );
}

export default Navbar;
